import {
  IPaymentService,
  PaymentFrequency,
  PaymentMethod,
  PaymentPlanCalculation,
  PaymentRequest,
  PaymentResult,
  PaymentVerification,
  RefundResult,
} from '../../domain/services/IPaymentService';
import axios, { type AxiosInstance } from 'axios';
import { z } from 'zod';
import { captureMessage } from '@sentry/nextjs';
import { trackConsumerEvent } from '@/libs/analytics/consumerEvents';
import { normalizeMoneyString } from '@/shared/utils/price';

const PAYMENT_OUTCOME_TELEMETRY_PREFIX = 'motito.telemetry.payment_outcome';
const emittedPaymentOutcomes = new Set<string>();

function hasEmittedPaymentOutcome(key: string): boolean {
  if (emittedPaymentOutcomes.has(key)) {
    return true;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markPaymentOutcomeEmitted(key: string): void {
  emittedPaymentOutcomes.add(key);
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Ignore storage quota/privacy mode failures.
  }
}

export interface PaymentInitializationParams {
  provider: string;
  method: string;
  phoneNumber: string;
  intent: 'checkout' | 'installment' | 'advance' | 'payoff' | 'additional_charge';
  autoDebitAuthorized?: boolean;
  amount?: string;
  context?: {
    checkout_session_id?: string;
    order_id?: string;
    installment_id?: string;
    charge_id?: string;
    amount?: string;
    payment_plan_id?: string;
    recovery_option_id?: string;
    recovery_relief_id?: string;
  };
  metadata?: Record<string, unknown>;
  idempotencyKey?: string;
}

export interface PaymentInitializationResult {
  paymentId: string;
  status: string;
  providerReference: string;
  instructions: {
    message: string;
    amount: string;
    reference: string;
    fallbackCode?: string;
  };
  appliedTo?: string[];
  scheduleEffect?: {
    installmentsCovered: number;
    nextDue: string;
    remainingBalance: string;
    planStatus: string;
  };
  pollingInterval: number;
  timeoutSeconds: number;
  /**
   * Indicates if user needs to manually approve the payment on their phone.
   * - true: User needs to approve on phone (show countdown + button)
   * - false: Auto-debit will happen (show simple spinner)
   * - undefined: Backend doesn't provide this (use fallback heuristic)
   */
  requiresUserApproval?: boolean;
}

export interface PaymentStatusResult {
  paymentId: string;
  checkoutSessionId?: string;
  status: string;
  providerStatus: string;
  amount: number;
  completedAt?: string;
  providerReference: string;
  receiptUrl?: string;
}

export type CustomerPaymentOutcome =
  | 'ready'
  | 'awaiting_customer'
  | 'processing'
  | 'confirming'
  | 'received_repairing'
  | 'completed'
  | 'returned'
  | 'retryable'
  | 'unavailable';

export type CustomerPaymentDecision =
  | 'available'
  | 'action_required'
  | 'processing'
  | 'completed'
  | 'unavailable';

export interface PaymentStatusPollResult {
  paymentId: string;
  paymentReference: string;
  status: string;
  statusDisplay: string;
  provider: string;
  amount: number;
  currency: string;
  providerReference: string;
  nextPollAfter: string;
  requiresAction: boolean;
  requiresUserApproval?: boolean;
  customerPaymentOutcome?: CustomerPaymentOutcome;
  customerPaymentDecision?: CustomerPaymentDecision;
}

export class ApiPaymentService implements IPaymentService {
  private readonly apiClient:AxiosInstance;

  constructor(
    private readonly baseUrl: string,
    private readonly onWalletChanged?: () => void | Promise<void>,
  ) {
    this.baseUrl = baseUrl;
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async initializePayment(
    params: PaymentInitializationParams
  ): Promise<PaymentInitializationResult> {
    // Emit before the API call so the funnel captures user intent even when
    // initialization fails (network error, 4xx, 5xx).
    trackConsumerEvent({
      eventType: 'payment_initiated',
      checkoutSessionId: params.context?.checkout_session_id,
      orderId: params.context?.order_id,
      properties: {
        checkout_session_id: params.context?.checkout_session_id || '',
        provider: params.provider || '',
        amount: Number(params.amount ?? params.context?.amount ?? 0),
        currency: 'GHS',
      },
    });

    const response = await this.apiClient.post(
      '/v1/payments/initialize',
      {
        intent: params.intent || 'checkout',
        context: params.context,
        payment_provider: params.provider,
        payment_method: params.method,
        phone_number: params.phoneNumber,
        idempotency_key: params.idempotencyKey || this.generateIdempotencyKey(),
        auto_debit_authorized: params.autoDebitAuthorized ?? true,
        ...(params.metadata ? { metadata: params.metadata } : {}),
      },
      {
        // headers: { Authorization: `Bearer ${this.getAuthToken()}` },
      }
    );

    return {
      paymentId: response.data.payment_id,
      status: response.data.status,
      providerReference: response.data.provider_reference,
      instructions: {
        message: response.data.instructions.message,
        amount: response.data.instructions.amount,
        reference: response.data.instructions.reference,
        fallbackCode: response.data.instructions.fallback_code,
      },
      appliedTo: response.data.applied_to,
      scheduleEffect: response.data.schedule_effect,
      pollingInterval: response.data.polling_interval || 5,
      timeoutSeconds: response.data.timeout_seconds || 60,
      requiresUserApproval: response.data.requires_user_approval,
    };
  }

  async getPaymentStatus(paymentId: string): Promise<PaymentStatusResult> {
    // NOTE: Staging currently requires a trailing slash for this endpoint; keep it
    // until backend is deployed with slashless support.
    const response = await this.apiClient.get(`/v1/payments/${encodeURIComponent(paymentId)}/`, { timeout: 15_000 });
    if (typeof response.data.payment_id !== 'string' || typeof response.data.checkout_session_id !== 'string'
      || !Number.isFinite(parseFloat(response.data.amount))) throw new Error('Payment ownership could not be verified');

    return {
      paymentId: response.data.payment_id,
      checkoutSessionId: response.data.checkout_session_id,
      status: response.data.status,
      providerStatus: response.data.provider_status,
      amount: parseFloat(response.data.amount),
      completedAt: response.data.completed_at,
      providerReference: response.data.provider_reference,
      receiptUrl: response.data.receipt_url,
    };
  }

  async getPaymentStatusPoll(
    identifier: string,
    timeoutMs?: number,
  ): Promise<PaymentStatusPollResult> {
    const response = await this.apiClient.get(
      `/v1/payments/${encodeURIComponent(identifier)}/status`,
      timeoutMs === undefined ? {} : { timeout: timeoutMs },
    );

    const customerPaymentOutcome = normalizeCustomerPaymentOutcome(
      response.data.customer_payment_outcome,
    );
    const hasCustomerDecisionField = Object.prototype.hasOwnProperty.call(
      response.data,
      'customer_payment_decision',
    );
    const hasCustomerOutcomeField = Object.prototype.hasOwnProperty.call(
      response.data,
      'customer_payment_outcome',
    );
    const result: PaymentStatusPollResult = {
      paymentId: response.data.payment_id,
      paymentReference: response.data.payment_reference,
      status: response.data.status,
      statusDisplay: response.data.status_display,
      provider: response.data.provider,
      amount: typeof response.data.amount === 'number' ? response.data.amount : parseFloat(response.data.amount),
      currency: response.data.currency,
      providerReference: response.data.provider_reference,
      nextPollAfter: response.data.next_poll_after,
      requiresAction: response.data.requires_action,
      requiresUserApproval: response.data.requires_user_approval,
      customerPaymentOutcome,
      customerPaymentDecision: normalizeCustomerPaymentDecision(
        response.data.customer_payment_decision,
        response.data.customer_payment_outcome,
        hasCustomerDecisionField,
        hasCustomerOutcomeField,
      ),
    };

    const statusUpper = String(result.status || '').toUpperCase();
    const hasCustomerDecision = result.customerPaymentDecision !== undefined;
    const paymentSucceeded = hasCustomerDecision
      ? result.customerPaymentDecision === 'completed'
      : ['SUCCESS', 'SUCCEEDED', 'COMPLETED'].includes(statusUpper);
    const paymentFailed = ['FAILED', 'EXPIRED', 'REJECTED', 'CANCELLED'].includes(
      statusUpper,
    ) && (
      !hasCustomerDecision || result.customerPaymentDecision === 'unavailable'
    );
    if (paymentSucceeded) {
      const dedupeBase = String(result.paymentReference || result.paymentId || '').trim();
      const dedupeKey = dedupeBase ? `${PAYMENT_OUTCOME_TELEMETRY_PREFIX}:${dedupeBase}:succeeded` : '';
      if (dedupeKey && !hasEmittedPaymentOutcome(dedupeKey)) {
        markPaymentOutcomeEmitted(dedupeKey);
        trackConsumerEvent({
          eventType: 'payment_succeeded',
          paymentReference: result.paymentReference,
          properties: {
            payment_reference: result.paymentReference || '',
            provider: result.provider || '',
            amount: result.amount,
            currency: result.currency || 'GHS',
          },
        });
      }
    } else if (paymentFailed) {
      const dedupeBase = String(result.paymentReference || result.paymentId || '').trim();
      const dedupeKey = dedupeBase ? `${PAYMENT_OUTCOME_TELEMETRY_PREFIX}:${dedupeBase}:failed` : '';
      if (dedupeKey && !hasEmittedPaymentOutcome(dedupeKey)) {
        markPaymentOutcomeEmitted(dedupeKey);
        trackConsumerEvent({
          eventType: 'payment_failed',
          paymentReference: result.paymentReference,
          properties: {
            payment_reference: result.paymentReference || '',
            provider: result.provider || '',
            amount: result.amount,
            currency: result.currency || 'GHS',
            failure_code: statusUpper,
          },
        });
      }
    }

    return result;
  }

  async getUpcomingPayments(_userId: string, limit: number = 20): Promise<UpcomingPaymentsResponse> {
    const response = await this.apiClient.get(
      '/v1/users/me/payments/upcoming',
      {
        params: { limit },
        // headers: { Authorization: `Bearer ${this.getAuthToken()}` },
      }
    );
    return response.data;
  }

  calculatePaymentPlan(
    _amount: number,
    _frequency: PaymentFrequency,
    _duration: number
  ): PaymentPlanCalculation {
    return {} as any;
  }

  getPaymentMethods(): Promise<PaymentMethod[]> {
    return Promise.resolve([]);
  }

  processPayment(_paymentRequest: PaymentRequest): Promise<PaymentResult> {
    return Promise.resolve({} as any);
  }

  refundPayment(_transactionId: string, _amount?: number): Promise<RefundResult> {
    return Promise.resolve({} as any);
  }

  verifyPayment(_transactionId: string): Promise<PaymentVerification> {
    return Promise.resolve({} as any);
  }

  async getStoreCreditBalance(context?: StoreCreditBalanceContext): Promise<StoreCreditBalanceResponse> {
    if (context?.checkout_session_id && context?.order_id) {
      throw new Error('Store credit balance accepts only one purchase context');
    }
    const response = await this.apiClient.get('/v1/payments/store-credits/balance', {
      timeout: 10_000,
      ...(context ? { params: context } : {}),
    });
    return normalizeStoreCreditBalanceResponse(response.data);
  }

  async getStoreCreditHistory(pageUrl?: string): Promise<StoreCreditHistoryResponse> {
    const response = await this.apiClient.get(this.resolveStoreCreditHistoryPath(pageUrl), {
      timeout: 10_000,
    });
    return normalizeStoreCreditHistoryResponse(response.data);
  }

  async applyStoreCredit(
    checkoutSessionId: string,
    amount?: string,
    creditIds?: string[]
  ): Promise<StoreCreditApplyResponse> {
    const response = await this.apiClient.post('/v1/payments/store-credits/apply', {
      checkout_session_id: checkoutSessionId,
      ...(amount ? { amount } : {}),
      ...(creditIds?.length ? { credit_ids: creditIds } : {}),
    });
    const result = normalizeStoreCreditApplyResponse(response.data);
    this.notifyWalletChanged();
    return result;
  }

  async removeStoreCreditApplication(applicationId: string): Promise<void> {
    await this.apiClient.delete(`/v1/payments/store-credits/applications/${applicationId}`);
    this.notifyWalletChanged();
  }

  async previewStoreCreditPlanPayment(
    request: StoreCreditPlanPaymentPreviewRequest
  ): Promise<StoreCreditPlanPaymentPreviewResponse> {
    const response = await this.apiClient.get('/v1/payments/store-credits/plan-payments/preview', {
      ...(request.consentProvider ? { timeout: 10_000 } : {}),
      params: {
        order_id: request.orderId,
        intent: request.intent,
        ...(request.amount ? { amount: request.amount } : {}),
        ...(request.installmentId ? { installment_id: request.installmentId } : {}),
        ...(request.applicationId ? { application_id: request.applicationId } : {}),
        ...(request.consentProvider ? { consent_provider: request.consentProvider } : {}),
        ...(request.consentAgreementId ? { consent_agreement_id: request.consentAgreementId } : {}),
        ...(request.creditIds?.length ? { credit_ids: request.creditIds } : {}),
      },
    });
    return normalizeStoreCreditPlanPaymentPreview(response.data);
  }

  async applyStoreCreditPlanPayment(
    request: StoreCreditPlanPaymentApplyRequest
  ): Promise<StoreCreditPlanPaymentApplyResponse | StoreCreditPlanPaymentConsentPending> {
    const payload = {
      order_id: request.orderId,
      intent: request.intent,
      idempotency_key: request.idempotencyKey,
      ...(request.amount ? { amount: request.amount } : {}),
      ...(request.installmentId ? { installment_id: request.installmentId } : {}),
      ...(request.creditIds?.length ? { credit_ids: request.creditIds } : {}),
      ...(request.consentProvider ? { consent_provider: request.consentProvider } : {}),
      ...(request.expectedWalletAmount ? { expected_wallet_amount: request.expectedWalletAmount } : {}),
      ...(request.consentAgreementId ? { consent_agreement_id: request.consentAgreementId } : {}),
      ...(request.refreshConsent !== undefined ? { refresh_consent: request.refreshConsent } : {}),
    };
    const endpoint = '/v1/payments/store-credits/plan-payments/apply';
    const response = request.consentProvider
      ? await this.apiClient.post(endpoint, payload, { timeout: 30_000 })
      : await this.apiClient.post(endpoint, payload);
    if (response.status === 202) return normalizePlanWalletConsentPending(response.data);
    const result = normalizeStoreCreditPlanPaymentApply(response.data);
    this.notifyWalletChanged();
    return result;
  }

  async getCompletedStoreCreditPlanPayment(
    request: StoreCreditPlanPaymentApplyRequest
  ): Promise<StoreCreditPlanPaymentApplyResponse | null> {
    try {
      const response = await this.apiClient.get('/v1/payments/store-credits/plan-payments/apply', {
        timeout: 10_000,
        params: {
          order_id: request.orderId,
          intent: request.intent,
          idempotency_key: request.idempotencyKey,
          ...(request.intent === 'advance' && request.amount ? { amount: request.amount } : {}),
          ...(request.installmentId ? { installment_id: request.installmentId } : {}),
        },
      });
      const result = normalizeStoreCreditPlanPaymentApply(response.data);
      this.notifyWalletChanged();
      return result;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      throw error;
    }
  }

  async getPaymentPlanRecommendations(_request: any) {}

  private notifyWalletChanged(): void {
    // A display refresh must never turn a confirmed payment into a failed payment.
    void Promise.resolve().then(() => this.onWalletChanged?.()).catch(() => {
      captureMessage('Shopping Wallet refresh failed after confirmed change', { level: 'warning' });
    });
  }

  async calculatePSSPayment(_request: any) {}

  private generateIdempotencyKey(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getAuthToken(): string {
    // In a real implementation, this would get the token from your auth store
    return '';
  }

  private resolveStoreCreditHistoryPath(pageUrl?: string): string {
    const historyPath = '/v1/payments/store-credits/history';
    if (!pageUrl) {
      return historyPath;
    }

    try {
      if (pageUrl.startsWith('?')) {
        return `${historyPath}${pageUrl}`;
      }

      const url = new URL(pageUrl, 'https://motito.local/');
      const normalizedPath = url.pathname.replace(/^\/api\/proxy(?=\/)/, '');
      return `${normalizedPath}${url.search}`;
    } catch {
      return pageUrl;
    }
  }
}


export interface UpcomingPaymentsResponse {
  results: UpcomingPayment[];
  summary: UpcomingPaymentsSummary;
  payment_calendar: UpcomingPaymentCalendarEntry[];
}

export interface UpcomingPaymentCalendarEntry {
  date: string;
  payments: UpcomingPayment[];
}

export interface UpcomingPayment {
  id: string;
  order_id: string;
  order_number: string;
  product_name: string;
  amount: string;
  scheduled_amount?: string;
  currency: string;
  due_date: string;
  status: string;
  payment_number: number;
  total_payments: number;
  payment_method: string;
  auto_debit: boolean;
  days_until_due: number;
  late_fee: string;
  can_pay_early: boolean;
  early_payment_discount: string;
}

export interface UpcomingPaymentsSummary {
  total_upcoming: number;
  total_amount: string;
  due_this_week: number;
  due_this_month: number;
  overdue_count: number;
  auto_debit_enabled: number;
}

// Store Credit / Shopping Wallet types

export interface WalletEligibleCategory {
  id: string;
  name: string;
}

export interface WalletCredit {
  id: string;
  amount: string;
  currency: string;
  remaining_amount: string;
  minimum_purchase_amount?: string | null;
  redemption_scope?: string;
  eligible_purchase_started_at?: string | null;
  eligible_product_category_ids?: string[];
  eligible_product_categories?: WalletEligibleCategory[];
  reason?: string;
  status?: string;
  source?: string;
  expires_at: string | null;
  issued_at?: string;
  created_at?: string;
  order_id?: string;
  description?: string;
}

export type StoreCreditBalanceContext =
  | { checkout_session_id?: string; order_id?: never }
  | { order_id?: string; checkout_session_id?: never };

export interface StoreCreditBalanceResponse {
  balance: string;
  pss_initial_available_balance?: string;
  purchase_available_balance?: string | null;
  currency: string;
  active_credits?: number;
  credits: WalletCredit[];
  expiring_soon?: {
    credit_id: string;
    amount: string;
    expiry_date: string;
  } | null;
}

export interface WalletCreditUsageHistoryEntry {
  application_id: string;
  amount_used: string;
  applied_at: string;
  order_id?: string | null;
}

export interface WalletHistoryCredit extends WalletCredit {
  usage_history: WalletCreditUsageHistoryEntry[];
}

export interface StoreCreditHistoryResponse {
  credits: WalletHistoryCredit[];
  pagination?: {
    count: number;
    next: string | null;
    previous: string | null;
    page_size: number;
  } | null;
}

export interface StoreCreditApplyResponse {
  application_id: string;
  amount_applied: string;
  currency: string;
  remaining_checkout_amount: string;
  credit_ids_used: string[];
}

export type StoreCreditPlanPaymentIntent = 'installment' | 'advance' | 'payoff';

export interface StoreCreditPlanPaymentRequest {
  orderId: string;
  intent: StoreCreditPlanPaymentIntent;
  amount?: string;
  installmentId?: string;
  creditIds?: string[];
}

export interface StoreCreditPlanPaymentPreviewRequest
  extends StoreCreditPlanPaymentRequest {
  applicationId?: string;
  consentProvider?: 'mtn';
  consentAgreementId?: string;
}

export interface StoreCreditPlanPaymentApplyRequest extends StoreCreditPlanPaymentRequest {
  idempotencyKey: string;
  consentProvider?: 'mtn';
  expectedWalletAmount?: string;
  consentAgreementId?: string;
  refreshConsent?: boolean;
}

export interface StoreCreditPlanPaymentConsent {
  status: 'pending' | 'active' | 'required';
  provider: 'mtn_momo';
  agreement_id: string | null;
}

export interface StoreCreditPlanPaymentConsentPending {
  status: 'pending';
  provider: 'mtn_momo';
  agreement_id: string;
  order_id: string;
  payment_plan_id: string;
  intent: StoreCreditPlanPaymentIntent;
  selected_payment_amount: string;
  wallet_amount: string;
  currency: string;
}

export function isPlanWalletConsentPending(
  response: StoreCreditPlanPaymentApplyResponse | StoreCreditPlanPaymentConsentPending,
): response is StoreCreditPlanPaymentConsentPending {
  return response.status === 'pending' && !('application_id' in response);
}

export interface StoreCreditPlanPaymentPendingApplication {
  application_id: string;
  payment_id: string;
  order_id: string;
  payment_plan_id: string;
  intent: StoreCreditPlanPaymentIntent;
  amount_applied: string;
  selected_payment_amount: string;
  remaining_selected_amount: string;
  currency: string;
  status: string;
  target_installment?: {
    id: string | null;
    status?: string | null;
    outstanding_amount?: string | null;
  };
  provider_remainder?: {
    payment_id: string;
    status: string;
  } | null;
}

export interface StoreCreditPlanPaymentPreviewResponse {
  consent?: StoreCreditPlanPaymentConsent;
  can_apply: boolean;
  blockers: string[];
  customer_payment_outcome?: CustomerPaymentOutcome;
  customer_payment_decision?: CustomerPaymentDecision;
  provider_payment_state?: 'available' | 'checking_provider' | 'unavailable';
  provider_payment_retry_after_seconds?: number;
  provider_payment?: {
    payment_id: string;
    status: string;
  };
  order_id?: string;
  order_number?: string;
  payment_plan_id?: string;
  intent?: StoreCreditPlanPaymentIntent;
  currency: string;
  eligible_balance?: string;
  selected_payment_amount?: string;
  max_applicable_amount?: string;
  remaining_selected_amount?: string;
  plan_balance_before?: string;
  plan_balance_after_wallet?: string;
  target_installment?: {
    id: string | null;
    installment_number: number | null;
    status: string | null;
    outstanding_amount: string | null;
    due_date: string | null;
  };
  pending_application?: StoreCreditPlanPaymentPendingApplication | null;
}

export interface StoreCreditPlanPaymentApplyResponse {
  application_id: string;
  payment_id: string;
  order_id: string;
  payment_plan_id: string;
  intent: StoreCreditPlanPaymentIntent;
  amount_applied: string;
  selected_payment_amount: string;
  remaining_selected_amount: string;
  currency: string;
  status: string;
  remaining_plan_balance: string;
  credits_used: Array<{
    credit_id: string;
    amount_used: string;
    remaining_amount: string;
    expires_at: string;
  }>;
  allocation?: Record<string, unknown>;
}

// Checkout Session Service
export interface CheckoutSessionParams {
  productId: string;
  quantity: number;
  paymentMethod?: string;
  paymentOption: string;
  autoDebitOptIn?: boolean;
  paymentPlan?: {
    durationMonths: number;
    frequency: string;
    kmpEnabled: boolean;
  };
  deliveryOption?: string;
  deliveryAddressId?: string;
}

export interface CheckoutSessionResult {
  sessionId: string;
  orderId: string;
  orderNumber: string;
  status: string;
  paymentBreakdown: {
    productTotal: string;
    kmpFee: string;
    processingFee: string;
    deliveryFee: string;
    totalAmount: string;
    currency: string;
    amountDueNow?: string;
    remainingBalance?: string;
    monthlyContribution?: string;
    monthlyKmpFee?: string;
  };
  selectedPlan?: any;
  paymentSchedule?: any[];
  nextStep: string;
  completionDate: string;
  expiresAt: string;
  transactionId?: string;
}

export interface CurrentCheckoutSession {
  id: string;
  session_id: string;
  account_id?: string;
  order_number: string;
  status: string;
  expires_at: string | null;
  created_at?: string;
  updated_at?: string;
  product?: Product;
  items?: any[];
  payment_breakdown: PaymentBreakdown & { requires_delivery_fee_invoice?: boolean };
  selected_plan?: any;
  payment_schedule?: any[];
  available_payment_methods: AvailablePaymentMethod[];
  auto_debit: AutoDebit & { next_execution_at?: string };
  store_credit_application?: { id: string; amount: string } | null;
  delivery?: {
    delivery_option?: string | null;
    delivery_address_id?: string | null;
    delivery_note?: string | null;
    pickup_note?: string | null;
    delivery_preferences?: Record<string, unknown>;
  };
  next_step?: string;
  completion_date?: string | null;
}

export interface AutoDebit {
  available: boolean;
  enabled: boolean;
}

export interface AvailablePaymentMethod {
  type: string;
  provider?: string | null;
  display_name?: string; // legacy/mock
  available?: boolean;
  is_default: boolean;
  requires_phone: boolean;
  auto_debit_available: boolean;
}

export interface PaymentBreakdown {
  product_total: string;
  amount_due_now: string;
  remaining_balance: string;
  currency: string;
  kmp_fee?: string;
  processing_fee?: string;
  delivery_fee?: string;
  total_amount?: string;
  monthly_contribution?: string; // legacy/mock
  monthly_kmp_fee?: string; // legacy/mock
}

export interface PaymentPlan {
  payment_method: string;
  duration_months: number;
  frequency: string;
  kmp_enabled: boolean;
  pss_provider: string;
}

export interface Product {
  id?: string;
  name: string;
  price: string;
  quantity: number;
  image_url: string;
  vendor: string;
  shop_logo?: string;
  shop_name?: string;
}

export type CheckoutConsentProvider = 'mtn' | 'telecel';

export interface CheckoutConsentRequest {
  payment_provider: CheckoutConsentProvider;
  phone_number: string;
}

const checkoutConsentSchema = z.object({
  status: z.enum(['required', 'pending', 'active', 'fallback', 'expired', 'completed']),
  provider: z.enum(['mtn_momo', 'telecel_momo']).nullable(),
  can_finalize: z.boolean(),
  auto_debit_active: z.boolean().nullable(),
});

export type CheckoutConsentResult = z.infer<typeof checkoutConsentSchema>;

export class CheckoutSessionService {
  private readonly baseUrl: string;
  private readonly apiClient;

  constructor(
    baseUrl: string = process.env.NEXT_PUBLIC_API_BASE_URL ||
      'https://api-staging.motito.co'
  ) {
    this.baseUrl = baseUrl;
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async createSession(
    params: CheckoutSessionParams
  ): Promise<CheckoutSessionResult> {
    const response = await this.apiClient.post(
      '/v1/checkout-sessions',
      {
        product_id: params.productId,
        quantity: params.quantity,
        payment_option: params.paymentOption,
        ...(params.autoDebitOptIn !== undefined
          ? { auto_debit_opt_in: params.autoDebitOptIn }
          : {}),
        payment_plan: params.paymentPlan
          ? {
              duration_months: params.paymentPlan.durationMonths,
              frequency: params.paymentPlan.frequency,
              kmp_enabled: params.paymentPlan.kmpEnabled,
            }
          : undefined,
        method: params.deliveryOption,
        delivery_address_id: params.deliveryAddressId,
      },
    );

    return this.mapCheckoutSessionResponse(response.data);
  }

  async getSession(sessionId: string): Promise<CurrentCheckoutSession> {
    const response = await this.apiClient.get(
      `/v1/checkout-sessions/${encodeURIComponent(sessionId)}`, { timeout: 15_000 },
    );
 
    return response.data;
  }

  async getRecurringConsent(sessionId: string): Promise<CheckoutConsentResult> {
    const response = await this.apiClient.get(
      `/v1/checkout-sessions/${sessionId}/recurring-consent/`,
      { timeout: 10_000 },
    );
    return checkoutConsentSchema.parse(response.data);
  }

  async requestRecurringConsent(
    sessionId: string,
    request: CheckoutConsentRequest,
  ): Promise<CheckoutConsentResult> {
    const response = await this.apiClient.post(
      `/v1/checkout-sessions/${sessionId}/recurring-consent/`,
      request,
      { timeout: 30_000 },
    );
    return checkoutConsentSchema.parse(response.data);
  }

  async updateSession(
    sessionId: string,
    updates: any
  ): Promise<CheckoutSessionResult> {
    const response = await this.apiClient.patch(
      `/v1/checkout-sessions/${sessionId}`,
      updates
    );

    return this.mapCheckoutSessionResponse(response.data);
  }

  async createOrderFromCheckoutSession(payload: {
    checkoutSessionId: string;
    paymentMethod: 'direct_payment' | 'paysmallsmall';
    deliveryMethod?: 'HOME_DELIVERY' | 'COLLECTION_POINT';
    deliveryAddressId?: string | null;
    deliveryPreferences?: Record<string, unknown>;
    deliveryNote?: string | null;
    pickupNote?: string | null;
    deliveryPhone?: string | null;
  }): Promise<CheckoutOrderResult> {
    const response = await this.apiClient.post('/v1/orders', {
      checkout_session_id: payload.checkoutSessionId,
      payment_method: payload.paymentMethod,
      delivery_method: payload.deliveryMethod,
      delivery_address_id: payload.deliveryAddressId,
      delivery_preferences: payload.deliveryPreferences,
      delivery_note: payload.deliveryNote,
      pickup_note: payload.pickupNote,
      delivery_phone: payload.deliveryPhone,
    });

    return response.data;
  }

  async listCollectionPoints(params: {
    region: string;
    search?: string;
    pointType?: string;
    active?: boolean;
  }): Promise<Array<{
    id: string;
    code: string;
    name: string;
    region: string;
    city: string;
    point_type: string;
    status: string;
    operating_hours: Record<string, unknown>;
    is_active: boolean;
    metadata: Record<string, unknown>;
  }>> {
    const response = await this.apiClient.get('/v1/collection-points', {
      params: {
        region: params.region,
        search: params.search,
        point_type: params.pointType,
        active: typeof params.active === 'boolean' ? String(params.active) : undefined,
      },
    });

    const payload = response.data;
    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && Array.isArray(payload.results)) {
      return payload.results;
    }
    return [];
  }

  private mapCheckoutSessionResponse(data: any): CheckoutSessionResult {
    return {
      sessionId: data.session_id,
      orderId: data.order_id ?? data.session_id,
      orderNumber: data.order_number,
      status: data.status,
      paymentBreakdown: {
        productTotal: data.payment_breakdown?.product_total || '0',
        kmpFee: data.payment_breakdown?.kmp_fee || '0',
        processingFee: data.payment_breakdown?.processing_fee || '0',
        deliveryFee: data.payment_breakdown?.delivery_fee || '0',
        totalAmount: data.payment_breakdown?.total_amount || '0',
        currency: data.payment_breakdown?.currency || 'GHS',
        amountDueNow: data.payment_breakdown?.amount_due_now,
        remainingBalance: data.payment_breakdown?.remaining_balance,
        monthlyContribution: data.payment_breakdown?.monthly_contribution,
        monthlyKmpFee: data.payment_breakdown?.monthly_kmp_fee,
      },
      selectedPlan: data.selected_plan,
      paymentSchedule: data.payment_schedule,
      nextStep: data.next_step,
      completionDate: data.completion_date,
      expiresAt: data.expires_at,
      transactionId: data.transaction_id,
    };
  }

  private getAuthToken(): string {
    return '';
  }
}

function unwrapApiEnvelope<T>(payload: unknown): T {
  const raw = (payload ?? {}) as Record<string, unknown>;
  const data = raw.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as T;
  }
  return payload as T;
}

function asString(value: unknown, fallback: string = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
}

function normalizeWalletCredit(rawCredit: unknown): WalletCredit {
  const credit = (rawCredit ?? {}) as Record<string, unknown>;
  return {
    id: asString(credit.id),
    amount: asString(credit.amount, '0.00'),
    currency: asString(credit.currency, 'GHS'),
    remaining_amount: asString(credit.remaining_amount, '0.00'),
    minimum_purchase_amount:
      typeof credit.minimum_purchase_amount === 'string' ? credit.minimum_purchase_amount : null,
    redemption_scope: typeof credit.redemption_scope === 'string' ? credit.redemption_scope : undefined,
    eligible_purchase_started_at:
      credit.eligible_purchase_started_at === null || typeof credit.eligible_purchase_started_at === 'string'
        ? credit.eligible_purchase_started_at : undefined,
    eligible_product_category_ids:
      Array.isArray(credit.eligible_product_category_ids)
      && credit.eligible_product_category_ids.every((id): id is string => typeof id === 'string')
        ? credit.eligible_product_category_ids : undefined,
    eligible_product_categories: normalizeWalletCategories(credit.eligible_product_categories),
    reason: asString(credit.reason),
    status: asString(credit.status),
    source: asString(credit.source),
    expires_at: credit.expires_at === null ? null : asString(credit.expires_at),
    issued_at: asString(credit.issued_at),
    created_at: asString(credit.created_at || credit.issued_at),
    order_id: asString(credit.order_id),
    description: asString(credit.description),
  };
}

function normalizeWalletCategories(value: unknown): WalletEligibleCategory[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((category): category is WalletEligibleCategory => (
    category !== null && typeof category === 'object'
    && typeof category.id === 'string' && category.id.trim().length > 0
    && typeof category.name === 'string' && category.name.trim().length > 0
  )).map(({ id, name }) => ({ id, name }));
}

function normalizeStoreCreditBalanceResponse(payload: unknown): StoreCreditBalanceResponse {
  const data = unwrapApiEnvelope<Record<string, unknown>>(payload);
  const rawCredits = Array.isArray(data.credits) ? data.credits : [];
  const rawExpiringSoon =
    data.expiring_soon && typeof data.expiring_soon === 'object' && !Array.isArray(data.expiring_soon)
      ? (data.expiring_soon as Record<string, unknown>)
      : null;

  return {
    balance: asString(data.total_balance ?? data.balance, '0.00'),
    purchase_available_balance:
      typeof data.purchase_available_balance === 'string' ? data.purchase_available_balance : null,
    ...(data.pss_initial_available_balance !== undefined
      ? {
          pss_initial_available_balance: asString(
            data.pss_initial_available_balance,
            '0.00'
          ),
        }
      : {}),
    currency: asString(data.currency, 'GHS'),
    active_credits:
      typeof data.active_credits === 'number' ? data.active_credits : rawCredits.length,
    credits: rawCredits.map(normalizeWalletCredit),
    expiring_soon: rawExpiringSoon
      ? {
          credit_id: asString(rawExpiringSoon.credit_id),
          amount: asString(rawExpiringSoon.amount, '0.00'),
          expiry_date: asString(rawExpiringSoon.expiry_date),
        }
      : null,
  };
}

function normalizeStoreCreditHistoryResponse(payload: unknown): StoreCreditHistoryResponse {
  const data = unwrapApiEnvelope<Record<string, unknown>>(payload);
  const rawCredits = Array.isArray(data.credits) ? data.credits : [];
  const rawPagination =
    data.pagination && typeof data.pagination === 'object' && !Array.isArray(data.pagination)
      ? (data.pagination as Record<string, unknown>)
      : null;

  return {
    credits: rawCredits.map((rawCredit) => {
      const credit = (rawCredit ?? {}) as Record<string, unknown>;
      const rawUsage = Array.isArray(credit.usage_history) ? credit.usage_history : [];

      return {
        ...normalizeWalletCredit(credit),
        usage_history: rawUsage.map((rawEntry) => {
          const entry = (rawEntry ?? {}) as Record<string, unknown>;
          return {
            application_id: asString(entry.application_id),
            amount_used: asString(entry.amount_used, '0.00'),
            applied_at: asString(entry.applied_at),
            order_id: asString(entry.order_id) || null,
          };
        }),
      };
    }),
    pagination: rawPagination
      ? {
          count: Number(rawPagination.count ?? 0),
          next: asString(rawPagination.next) || null,
          previous: asString(rawPagination.previous) || null,
          page_size: Number(rawPagination.page_size ?? 0),
        }
      : null,
  };
}

function normalizeStoreCreditApplyResponse(payload: unknown): StoreCreditApplyResponse {
  const data = unwrapApiEnvelope<Record<string, unknown>>(payload);
  const creditsUsed = Array.isArray(data.credits_used) ? data.credits_used : [];
  const target =
    data.target && typeof data.target === 'object' && !Array.isArray(data.target)
      ? (data.target as Record<string, unknown>)
      : null;

  return {
    application_id: asString(data.application_id),
    amount_applied: asString(data.total_applied ?? data.amount_applied, '0.00'),
    currency: asString(data.currency, 'GHS'),
    remaining_checkout_amount: asString(target?.new_total ?? data.remaining_checkout_amount, '0.00'),
    credit_ids_used: creditsUsed
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return asString((item as Record<string, unknown>).credit_id);
        }
        return '';
      })
      .filter(Boolean),
  };
}

function normalizePlanPaymentIntent(value: unknown): StoreCreditPlanPaymentIntent {
  return value === 'payoff' || value === 'installment' ? value : 'advance';
}

const KNOWN_CUSTOMER_PAYMENT_OUTCOMES = new Set<CustomerPaymentOutcome>([
  'ready',
  'awaiting_customer',
  'processing',
  'confirming',
  'received_repairing',
  'completed',
  'returned',
  'retryable',
  'unavailable',
]);

function normalizeCustomerPaymentOutcome(value: unknown): CustomerPaymentOutcome | undefined {
  const outcome = asString(value);
  return KNOWN_CUSTOMER_PAYMENT_OUTCOMES.has(outcome as CustomerPaymentOutcome)
    ? (outcome as CustomerPaymentOutcome)
    : undefined;
}

const KNOWN_CUSTOMER_PAYMENT_DECISIONS = new Set<CustomerPaymentDecision>([
  'available',
  'action_required',
  'processing',
  'completed',
  'unavailable',
]);

const CUSTOMER_PAYMENT_DECISION_BY_OUTCOME: Record<
  CustomerPaymentOutcome,
  CustomerPaymentDecision
> = {
  ready: 'available',
  awaiting_customer: 'action_required',
  processing: 'processing',
  confirming: 'processing',
  received_repairing: 'processing',
  completed: 'completed',
  returned: 'unavailable',
  retryable: 'available',
  unavailable: 'unavailable',
};

function normalizeCustomerPaymentDecision(
  decisionValue: unknown,
  outcomeValue: unknown,
  hasDecisionField = false,
  hasOutcomeField = false,
): CustomerPaymentDecision | undefined {
  const decision = asString(decisionValue);
  if (hasDecisionField) {
    return KNOWN_CUSTOMER_PAYMENT_DECISIONS.has(decision as CustomerPaymentDecision)
      ? (decision as CustomerPaymentDecision)
      : 'unavailable';
  }

  if (hasOutcomeField) {
    const outcome = normalizeCustomerPaymentOutcome(outcomeValue);
    return outcome ? CUSTOMER_PAYMENT_DECISION_BY_OUTCOME[outcome] : 'unavailable';
  }
  return undefined;
}

function normalizeStoreCreditPlanPaymentPreview(payload: unknown): StoreCreditPlanPaymentPreviewResponse {
  const data = unwrapApiEnvelope<Record<string, unknown>>(payload);
  const targetInstallment =
    data.target_installment && typeof data.target_installment === 'object' && !Array.isArray(data.target_installment)
      ? (data.target_installment as Record<string, unknown>)
      : null;
  const pendingApplication =
    data.pending_application && typeof data.pending_application === 'object' && !Array.isArray(data.pending_application)
      ? (data.pending_application as Record<string, unknown>)
      : null;
  const providerPayment =
    data.provider_payment && typeof data.provider_payment === 'object' && !Array.isArray(data.provider_payment)
      ? (data.provider_payment as Record<string, unknown>)
      : null;
  return {
    ...(data.consent !== undefined ? { consent: normalizePlanWalletConsent(data.consent) } : {}),
    can_apply: data.can_apply === true,
    blockers: Array.isArray(data.blockers) ? data.blockers.map((item) => asString(item)).filter(Boolean) : [],
    customer_payment_outcome: normalizeCustomerPaymentOutcome(data.customer_payment_outcome),
    customer_payment_decision: normalizeCustomerPaymentDecision(
      data.customer_payment_decision,
      data.customer_payment_outcome,
      Object.prototype.hasOwnProperty.call(data, 'customer_payment_decision'),
      Object.prototype.hasOwnProperty.call(data, 'customer_payment_outcome'),
    ),
    provider_payment_state:
      data.provider_payment_state === undefined
        ? undefined
        : data.provider_payment_state === 'available' ||
      data.provider_payment_state === 'checking_provider'
        ? data.provider_payment_state
        : 'unavailable',
    provider_payment_retry_after_seconds:
      typeof data.provider_payment_retry_after_seconds === 'number'
        ? data.provider_payment_retry_after_seconds
        : undefined,
    provider_payment: providerPayment
      ? {
          payment_id: asString(providerPayment.payment_id),
          status: asString(providerPayment.status),
        }
      : undefined,
    order_id: asString(data.order_id),
    order_number: asString(data.order_number),
    payment_plan_id: asString(data.payment_plan_id),
    intent: normalizePlanPaymentIntent(data.intent),
    currency: asString(data.currency, 'GHS'),
    eligible_balance: asString(data.eligible_balance, '0.00'),
    selected_payment_amount: asString(data.selected_payment_amount, '0.00'),
    max_applicable_amount: asString(data.max_applicable_amount, '0.00'),
    remaining_selected_amount: asString(data.remaining_selected_amount, '0.00'),
    plan_balance_before: asString(data.plan_balance_before, '0.00'),
    plan_balance_after_wallet: asString(data.plan_balance_after_wallet, '0.00'),
    target_installment: targetInstallment
      ? {
          id: asString(targetInstallment.id) || null,
          installment_number:
            typeof targetInstallment.installment_number === 'number'
              ? targetInstallment.installment_number
              : null,
          status: asString(targetInstallment.status) || null,
          outstanding_amount: asString(targetInstallment.outstanding_amount) || null,
          due_date: asString(targetInstallment.due_date) || null,
        }
      : undefined,
    pending_application: pendingApplication
      ? {
          application_id: asString(pendingApplication.application_id),
          payment_id: asString(pendingApplication.payment_id),
          order_id: asString(pendingApplication.order_id),
          payment_plan_id: asString(pendingApplication.payment_plan_id),
          intent: normalizePlanPaymentIntent(pendingApplication.intent),
          amount_applied: asString(pendingApplication.amount_applied, '0.00'),
          selected_payment_amount: asString(pendingApplication.selected_payment_amount, '0.00'),
          remaining_selected_amount: asString(pendingApplication.remaining_selected_amount, '0.00'),
          currency: asString(pendingApplication.currency, 'GHS'),
          status: asString(pendingApplication.status),
          target_installment:
            pendingApplication.target_installment &&
            typeof pendingApplication.target_installment === 'object' &&
            !Array.isArray(pendingApplication.target_installment)
              ? {
                  id:
                    asString(
                      (pendingApplication.target_installment as Record<string, unknown>).id,
                    ) || null,
                  status:
                    asString(
                      (pendingApplication.target_installment as Record<string, unknown>).status,
                    ) || null,
                  outstanding_amount:
                    asString(
                      (pendingApplication.target_installment as Record<string, unknown>)
                        .outstanding_amount,
                    ) || null,
                }
              : undefined,
          provider_remainder:
            pendingApplication.provider_remainder &&
            typeof pendingApplication.provider_remainder === 'object' &&
            !Array.isArray(pendingApplication.provider_remainder)
              ? {
                  payment_id: asString(
                    (pendingApplication.provider_remainder as Record<string, unknown>).payment_id,
                  ),
                  status: asString(
                    (pendingApplication.provider_remainder as Record<string, unknown>).status,
                  ),
                }
              : null,
        }
      : null,
  };
}

function normalizePlanWalletConsent(payload: unknown): StoreCreditPlanPaymentConsent {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) throw new Error();
  const data = payload as Record<string, unknown>;
  const agreementId = data.agreement_id;
  if (!['pending', 'active', 'required'].includes(String(data.status)) || data.provider !== 'mtn_momo'
      || (agreementId !== null && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(agreementId)))) {
    throw new Error();
  }
  return { status: data.status as StoreCreditPlanPaymentConsent['status'], provider: 'mtn_momo', agreement_id: agreementId as string | null };
}

function normalizePlanWalletConsentPending(payload: unknown): StoreCreditPlanPaymentConsentPending {
  const data = unwrapApiEnvelope<Record<string, unknown>>(payload);
  const consent = normalizePlanWalletConsent(data);
  if (consent.status !== 'pending' || !consent.agreement_id || !data.order_id || !data.payment_plan_id
      || !['installment', 'advance', 'payoff'].includes(String(data.intent))
      || !normalizeMoneyString(data.wallet_amount) || !normalizeMoneyString(data.selected_payment_amount)
      || !data.currency || data.application_id || data.payment_id) throw new Error();
  return { ...consent, status: 'pending', agreement_id: consent.agreement_id,
    order_id: asString(data.order_id), payment_plan_id: asString(data.payment_plan_id),
    intent: data.intent as StoreCreditPlanPaymentIntent, wallet_amount: asString(data.wallet_amount),
    selected_payment_amount: asString(data.selected_payment_amount), currency: asString(data.currency) };
}

function normalizeStoreCreditPlanPaymentApply(payload: unknown): StoreCreditPlanPaymentApplyResponse {
  const data = unwrapApiEnvelope<Record<string, unknown>>(payload);
  const creditsUsed = Array.isArray(data.credits_used) ? data.credits_used : [];

  return {
    application_id: asString(data.application_id),
    payment_id: asString(data.payment_id),
    order_id: asString(data.order_id),
    payment_plan_id: asString(data.payment_plan_id),
    intent: normalizePlanPaymentIntent(data.intent),
    amount_applied: asString(data.amount_applied, '0.00'),
    selected_payment_amount: asString(data.selected_payment_amount, '0.00'),
    remaining_selected_amount: asString(data.remaining_selected_amount, '0.00'),
    currency: asString(data.currency, 'GHS'),
    status: asString(data.status),
    remaining_plan_balance: asString(data.remaining_plan_balance, '0.00'),
    credits_used: creditsUsed.map((item) => {
      const credit = (item ?? {}) as Record<string, unknown>;
      return {
        credit_id: asString(credit.credit_id),
        amount_used: asString(credit.amount_used, '0.00'),
        remaining_amount: asString(credit.remaining_amount, '0.00'),
        expires_at: asString(credit.expires_at),
      };
    }),
    allocation:
      data.allocation && typeof data.allocation === 'object' && !Array.isArray(data.allocation)
        ? (data.allocation as Record<string, unknown>)
        : undefined,
  };
}

// PSS Calculation Service
export interface PSSPlansResponse {
  product: Record<string, unknown>;
  kmpPolicy: {
    available: boolean;
    basePercentage: number;
    minFee: string;
    currency: string;
  };
  userEligibility: {
    repScore: number;
    pssCreditEligible: boolean;
    pssCreditPercentage: number;
    earlyRedemptionThreshold: number;
    recommendedDuration: number;
    maxFinanceableAmount: string;
  };
  durationOptions: PSSDurationOption[];
}

export interface PSSDurationOption {
  months: number;
  [key: string]: unknown;
}

export interface CheckoutOrderResult {
  id: string;
  order_number: string;
  total_amount?: string | number | null;
  currency?: string | null;
  [key: string]: unknown;
}

export class PSSCalculationService {
  private readonly baseUrl: string;
  private readonly apiClient;

  constructor(
    baseUrl: string = process.env.NEXT_PUBLIC_API_BASE_URL ||
      'https://api-staging.motito.co'
  ) {
    this.baseUrl = baseUrl;
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async getPaymentPlans(productId: string): Promise<PSSPlansResponse> {
    const response = await this.apiClient.get(`/v1/products/${productId}/payment-plans`);

    return {
      product: response.data.product,
      kmpPolicy: response.data.kmp_policy,
      userEligibility: response.data.user_eligibility,
      durationOptions: response.data.duration_options,
    };
  }

  private getAuthToken(): string {
    return '';
  }
}
